import { Component, OnDestroy, OnInit, Signal, computed, effect, inject, signal, untracked } from '@angular/core'
import { LucideMessageCircle, LucideX } from '@lucide/angular'

import { AuthService } from '../../../auth/services/auth.service'
import { NavContextService } from '../../services/nav-context.service'
import { WelcomeSlotService } from '../../services/welcome-slot.service'
import { whatsAppHelpHref } from '../../utils/whatsapp-help.util'

export const HINT_SEEN = 'scios.whatsappHelp.hintSeen'
/** The hint waits for the page to settle, then stays long enough to read. */
export const HINT_AFTER_MS = 1500
export const HINT_FOR_MS = 10000
/** How long the hint waits for What's new to decide before asking for the visit anyway (its list
 *  may never arrive). Release 5, 31/10/2026. */
export const HINT_WAIT_MAX_MS = 8000

/**
 * The "Ask us on WhatsApp" link for whoever is signed in and what is on screen. Shared by the
 * button and, on phones narrower than 400px, the account panel's row (Release 5, 31/10/2026).
 */
export function injectWhatsAppHelpHref (): Signal<string> {
  const auth = inject(AuthService)
  const navCtx = inject(NavContextService)
  return computed(() => {
    const user = auth.currentUser()
    const versus = navCtx.versus()
    return whatsAppHelpHref({
      name: user ? `${user.first_name ?? ''} ${user.last_name ?? ''}` : null,
      entity: versus ? null : navCtx.entityLabel(),
      home: versus ? navCtx.entityLabel() : null,
      away: versus ? navCtx.entityLabel2() : null,
    })
  })
}

/**
 * The top bar's "Ask us on WhatsApp" (CC, 25/10/2026): an icon on a phone, "Ask us" from tablet
 * width up. The message starts with who is asking and the player, team or match on screen, so
 * the team in the shared inbox can answer without asking back. A one-time hint introduces it.
 */
@Component({
  selector: 'app-whatsapp-help-button',
  standalone: true,
  imports: [LucideMessageCircle, LucideX],
  template: `
    <a class="wa-btn" [href]="href()" target="_blank" rel="noopener" aria-label="Ask us on WhatsApp"
       title="Ask us on WhatsApp" (click)="hideHint()">
      <svg lucideMessageCircle [size]="20" aria-hidden="true"></svg>
      <span class="wa-btn__label">Ask us</span>
    </a>
    @if (hintShown()) {
      <div class="wa-hint" role="status">
        <span class="wa-hint__text">
          <strong>Questions? Ask us on WhatsApp</strong>
          <span>One tap opens WhatsApp with your message ready. A real person from SCIOS answers.</span>
        </span>
        <button type="button" class="wa-hint__close" aria-label="Close" (click)="hideHint()">
          <svg lucideX [size]="16" aria-hidden="true"></svg>
        </button>
      </div>
    }
  `,
  styles: [`
    :host {
      position: relative;
      display: inline-flex;
      --wa-green: #25D366;
      --wa-green-ink: #25D366;
    }
    :host-context(html[data-theme="light"]) { --wa-green-ink: #0B7A3E; }
    .wa-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      width: 40px;
      height: 40px;
      border-radius: 10px;
      border: 1.5px solid var(--wa-green);
      background: color-mix(in srgb, var(--wa-green) 12%, transparent);
      color: var(--wa-green-ink);
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      white-space: nowrap;
      box-sizing: border-box;
    }
    .wa-btn__label { display: none; }
    /* A thumb-sized target on phones (Release 5, 31/10/2026, SHELL-11). */
    @media (max-width: 767.98px) {
      .wa-btn { width: 44px; height: 44px; }
    }
    @media (min-width: 768px) {
      .wa-btn { width: auto; padding: 0 14px; }
      .wa-btn__label { display: inline; }
    }
    /* A sideways phone keeps the label (there is room) but gets the thumb height (Release 7,
       04/11/2026, SHELL2-05: the coarse-pointer half of the phone rule in _breakpoints.scss). */
    @media (min-width: 768px) and (pointer: coarse) and (max-height: 500px) {
      .wa-btn { height: 44px; }
    }
    @media (hover: hover) {
      .wa-btn:hover { background: color-mix(in srgb, var(--wa-green) 22%, transparent); }
    }
    .wa-btn:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }

    .wa-hint {
      position: absolute;
      top: calc(100% + 10px);
      right: -8px;
      z-index: 1003;
      width: min(280px, calc(100vw - 24px));
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 12px 8px 12px 14px;
      border-radius: 12px;
      background: var(--scios-text-primary);
      color: var(--scios-surface-1);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
      animation: wa-hint-in 200ms ease-out;
    }
    .wa-hint__text { display: flex; flex-direction: column; gap: 4px; font-size: 13px; line-height: 1.4; }
    .wa-hint__text strong { font-size: 14px; }
    .wa-hint__close {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      background: transparent;
      color: inherit;
      cursor: pointer;
    }
    @media (max-width: 767.98px), (pointer: coarse) and (max-height: 500px) { .wa-hint__close { width: 44px; height: 44px; } }
    @keyframes wa-hint-in {
      from { opacity: 0; transform: translateY(-4px); }
      to   { opacity: 1; transform: none; }
    }
    @media (prefers-reduced-motion: reduce) { .wa-hint { animation: none; } }
  `],
})
export class WhatsAppHelpButtonComponent implements OnInit, OnDestroy {
  private readonly welcome = inject(WelcomeSlotService)

  readonly hintShown = signal(false)
  private timers: ReturnType<typeof setTimeout>[] = []

  readonly href = injectWhatsAppHelpHref()

  /** The page has settled (HINT_AFTER_MS), and the longest wait for What's new is over. */
  private readonly settled = signal(false)
  private readonly waitedLongEnough = signal(false)
  private asked = false
  /** The tab went to the background while the hint was up: it was not read, so not "seen". */
  private hiddenWhileShown = false
  private readonly onVisibility = () => {
    if (this.hintShown() && document.visibilityState === 'hidden') this.hiddenWhileShown = true
  }

  constructor () {
    // One welcome item per visit (Release 5, 31/10/2026): ask for the slot only once What's new
    // has decided, so the hint never opens under its sheet.
    effect(() => {
      if (!this.settled() || this.asked) return
      if (!this.welcome.landingSettled() && !this.waitedLongEnough()) return
      untracked(() => this.tryShow())
    })
  }

  ngOnInit (): void {
    let seen = true
    try {
      seen = localStorage.getItem(HINT_SEEN) === '1'
    } catch { /* storage unavailable: skip the hint rather than show it every visit */ }
    if (seen) return
    this.timers.push(setTimeout(() => this.settled.set(true), HINT_AFTER_MS))
    this.timers.push(setTimeout(() => this.waitedLongEnough.set(true), HINT_WAIT_MAX_MS))
  }

  ngOnDestroy (): void {
    this.timers.forEach(clearTimeout)
    document.removeEventListener('visibilitychange', this.onVisibility)
  }

  private tryShow (): void {
    this.asked = true
    // A tab opened in the background, or another welcome item already had this visit: next visit.
    if (document.visibilityState === 'hidden' || !this.welcome.claim('whatsapp-hint')) return
    this.hintShown.set(true)
    this.hiddenWhileShown = false
    document.addEventListener('visibilitychange', this.onVisibility)
    this.timers.push(setTimeout(() => {
      // Seen only once it was actually on screen for its whole time (Release 5, 31/10/2026).
      if (!this.hiddenWhileShown) this.markSeen()
      this.close()
    }, HINT_FOR_MS))
  }

  /** ✕, or a tap on the button itself: they read it and answered it. */
  hideHint (): void {
    if (this.hintShown()) this.markSeen()
    this.close()
  }

  private close (): void {
    this.hintShown.set(false)
    document.removeEventListener('visibilitychange', this.onVisibility)
  }

  private markSeen (): void {
    try {
      localStorage.setItem(HINT_SEEN, '1')
    } catch { /* shown once more next visit at worst */ }
  }
}
